import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SimpleLocationPicker from '../components/SimpleLocationPicker';
import { useLocation } from '../context/LocationContext';
import { 
  Stethoscope, 
  Pill, 
  TestTube, 
  Activity, 
  User, 
  MapPin, 
  Award,
  Phone,
  ArrowRight,
  Heart,
  Shield,
  Clock
} from 'lucide-react';
import { mockOffers, generateWhatsAppLink } from '../mock/mockData';

const Home = () => {
  const { currentLocation, getServiceMessage } = useLocation();
  const [subscribeForm, setSubscribeForm] = useState({
    name: '',
    email: '',
    phone: '',
    age: ''
  });

  const handleSubscribe = (e) => {
    e.preventDefault();
    const whatsAppMessage = generateWhatsAppLink('subscription', {
      userName: subscribeForm.name,
      email: subscribeForm.email,
      phone: subscribeForm.phone,
      age: subscribeForm.age
    });
    window.open(whatsAppMessage, '_blank');
  };

  const quickLinks = [
    { name: 'Doctors', href: '/doctors', icon: Stethoscope, color: 'bg-blue-500', description: 'Find & Book Appointments' },
    { name: 'Pharmacy', href: '/pharmacy', icon: Pill, color: 'bg-green-500', description: 'Order Medicines' },
    { name: 'Lab Tests', href: '/lab', icon: TestTube, color: 'bg-purple-500', description: 'Book Lab Tests' },
    { name: 'Vitals', href: '/vitals', icon: Activity, color: 'bg-red-500', description: 'Track Health' },
    { name: 'Account', href: '/account', icon: User, color: 'bg-orange-500', description: 'Manage Profile' }
  ];

  const features = [
    {
      icon: Heart,
      title: 'Expert Care',
      description: 'Connect with verified doctors and healthcare professionals'
    },
    {
      icon: Shield,
      title: 'Secure & Safe',
      description: 'Your health data is protected with industry-standard security'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock healthcare assistance via WhatsApp'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Location Selector Banner - More Compact */}
      <section className="bg-orange-600 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <span className="text-white text-sm font-medium hidden md:block">Healthcare services in:</span>
              <div className="relative">
                <SimpleLocationPicker />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 to-orange-100 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-white/20 backdrop-blur-sm"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Your Health, <span className="text-orange-600">Our Priority</span>
                </h1>
                <p className="text-xl text-gray-600 max-w-lg">
                  Complete healthcare solutions at your fingertips. Connect with doctors, 
                  order medicines, book lab tests - all through WhatsApp.
                </p>
              </div>

              {/* Offer Chips */}
              <div className="flex flex-wrap gap-4">
                {mockOffers.map((offer) => (
                  <Badge key={offer.id} className="bg-orange-600 text-white px-4 py-2 text-sm font-semibold">
                    {offer.title} {offer.subtitle}
                  </Badge>
                ))}
              </div>

              {/* Location Services */}
              <div className="bg-white/90 backdrop-blur-sm p-4 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-gray-900 mb-2">Available in your area:</h4>
                <div className="space-y-1">
                  {currentLocation && getServiceMessage().length > 0 ? (
                    getServiceMessage().map((message, index) => (
                      <div key={index} className="text-sm text-gray-700">{message}</div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-700">
                      👨‍⚕️ Doctor consultations • 💊 Medicine delivery • 🧪 Lab tests
                    </div>
                  )}
                </div>
              </div>

              {/* CTA */}
              <Link to="/doctors">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-lg px-8 py-4">
                  Find Doctors Near You
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1666886573452-9dc8ce8f5cc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc1ODgxOTg5Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Healthcare Professional"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Healthcare Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {quickLinks.map((link) => (
              <Link key={link.name} to={link.href}>
                <Card className="group hover:shadow-lg transition-all duration-300 border-orange-100 hover:border-orange-300">
                  <CardContent className="p-6 text-center">
                    <div className={`${link.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <link.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{link.name}</h3>
                    <p className="text-sm text-gray-600">{link.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Widget */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-3xl text-gray-900">Become a Subscriber</CardTitle>
              <CardDescription className="text-lg text-gray-600">
                Get 50% off on lab tests, free home delivery, and priority healthcare support
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubscribe} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  placeholder="Full Name"
                  value={subscribeForm.name}
                  onChange={(e) => setSubscribeForm({...subscribeForm, name: e.target.value})}
                  required
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={subscribeForm.email}
                  onChange={(e) => setSubscribeForm({...subscribeForm, email: e.target.value})}
                  required
                />
                <Input
                  type="tel"
                  placeholder="Phone"
                  value={subscribeForm.phone}
                  onChange={(e) => setSubscribeForm({...subscribeForm, phone: e.target.value})}
                  required
                />
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                  Subscribe Free
                </Button>
              </form>
              <p className="text-xs text-gray-500 mt-4 text-center">
                * Free subscription with instant benefits. No hidden charges.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose GEETH365?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center border-orange-100 hover:border-orange-300 transition-colors">
                <CardContent className="p-8">
                  <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency & Medical Help Contact */}
      <section className="py-12 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Medical Emergency */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Medical Emergency</h2>
              <p className="text-red-100 mb-6">For immediate medical emergencies, contact local emergency services</p>
              
              <div className="space-y-3">
                <a href="tel:108" className="block">
                  <Button size="lg" className="w-full bg-white text-red-600 hover:bg-gray-100">
                    <Phone className="w-5 h-5 mr-2" />
                    Emergency Ambulance: 108
                  </Button>
                </a>
                <a href="tel:112" className="block">
                  <Button size="lg" className="w-full bg-white text-red-600 hover:bg-gray-100">
                    <Phone className="w-5 h-5 mr-2" />
                    Emergency Helpline: 112
                  </Button>
                </a>
                <a href="tel:100" className="block">
                  <Button size="lg" className="w-full bg-white text-red-600 hover:bg-gray-100">
                    <Phone className="w-5 h-5 mr-2" />
                    Police Emergency: 100
                  </Button>
                </a>
              </div>
            </div>

            {/* Medical Help & Consultation */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Medical Help & Consultation</h2>
              <p className="text-red-100 mb-6">For medical queries, health guidance, and doctor consultations</p>
              
              <div className="space-y-3">
                <a 
                  href="https://wa.me/917337541113?text=Need medical consultation and guidance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button size="lg" className="w-full bg-green-500 text-white hover:bg-green-600">
                    <Phone className="w-5 h-5 mr-2" />
                    Medical Help: 7337541113
                  </Button>
                </a>
                <a 
                  href="tel:7337541113"
                  className="block"
                >
                  <Button size="lg" className="w-full bg-white text-red-600 hover:bg-gray-100">
                    <Phone className="w-5 h-5 mr-2" />
                    Call for Medical Guidance
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;